import React, { useEffect, useState } from "react";
import { API, apiError, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Plug, Pencil, Trash2, CheckCircle2, XCircle, HelpCircle } from "lucide-react";

const EMPTY_FORM = {
  name: "",
  code: "",
  shop_domain: "",
  client_id: "",
  client_secret: "",
  spediamo_auth_code: "",
  sender: { name: "", address: "", address2: "", zip: "", city: "", province: "", phone: "", email: "" },
  parcel: { length: 300, width: 400, height: 50, weight: 1000, type: 0 },
  couriers: [
    { name: "SDA Express", service_code: "SDAEXP" },
    { name: "BRT Express", service_code: "BRTEXP" },
  ],
  default_courier: "SDAEXP",
  notify_customer: true,
};

function IntegrationKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/integrations/keys").then((r) => setKeys(r.data.keys)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await API.post("/integrations/keys", { name: name.trim() });
      setCreated(r.data);
      setName("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revocare la chiave "${k.name}" (${k.prefix}…)? Il gestionale smettera di leggere i dati.`)) return;
    try {
      await API.delete(`/integrations/keys/${k.id}`);
      toast.success(`Chiave "${k.name}" revocata`);
      if (created?.id === k.id) setCreated(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="integration-keys-card">
      <h2 className="text-lg font-bold">Integrazione gestionale — API di sola lettura</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Chiavi per il gestionale ordini/margini: accesso in <strong>sola lettura</strong> agli endpoint <span className="font-mono">/api/ext/*</span>{" "}
        con header <span className="font-mono">X-API-Key</span>. La chiave completa e visibile solo alla creazione. Formato dati: vedi INTEGRAZIONE.md.
      </p>
      <div className="mt-4 flex gap-2">
        <input
          data-testid="key-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome chiave (es. Gestionale MBO)"
          className="h-11 flex-1 rounded-lg border border-input px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        />
        <button
          data-testid="key-create-btn"
          onClick={create}
          disabled={busy || !name.trim()}
          className="rounded-lg bg-blue-600 px-5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? "Creazione…" : "Genera chiave"}
        </button>
      </div>
      {created && (
        <div className="mt-4 rounded-lg border border-teal-300 bg-teal-50 p-4" data-testid="key-created-banner">
          <div className="text-xs font-bold uppercase tracking-wider text-teal-800">
            Chiave creata — copiala ora: non sara piu visibile
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-white px-3 py-2 font-mono text-xs" data-testid="key-created-value">{created.api_key}</code>
            <button
              data-testid="key-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(created.api_key);
                toast.success("Chiave copiata");
              }}
              className="rounded-lg border border-teal-400 px-3 py-2 text-xs font-bold text-teal-800 hover:bg-teal-100"
            >
              Copia
            </button>
          </div>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {keys.length === 0 && (
          <div className="text-sm text-muted-foreground" data-testid="keys-empty">Nessuna chiave attiva.</div>
        )}
        {keys.map((k) => (
          <div key={k.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid={`key-row-${k.id}`}>
            <div>
              <div className="text-sm font-bold">
                {k.name} <span className="font-mono text-xs text-muted-foreground">{k.prefix}…</span>
              </div>
              <div className="text-xs text-muted-foreground">
                Creata: {formatDateTime(k.created_at)} · Ultimo uso: {k.last_used_at ? formatDateTime(k.last_used_at) : "mai"}
              </div>
            </div>
            <button
              data-testid={`key-revoke-${k.id}`}
              onClick={() => revoke(k)}
              className="rounded-lg border border-rose-300 px-3 py-2 text-xs font-bold text-rose-700 hover:bg-rose-50"
            >
              Revoca
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConnStatus({ status, testid }) {
  if (status === "connected")
    return (
      <span data-testid={testid} className="flex items-center gap-1.5 rounded-md border border-teal-300 bg-teal-100 px-2 py-1 text-xs font-semibold text-teal-800">
        <CheckCircle2 size={14} /> Connesso
      </span>
    );
  if (status === "error")
    return (
      <span data-testid={testid} className="flex items-center gap-1.5 rounded-md border border-rose-300 bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-800">
        <XCircle size={14} /> Errore
      </span>
    );
  return (
    <span data-testid={testid} className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
      <HelpCircle size={14} /> Da verificare
    </span>
  );
}

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | store
  const [deleting, setDeleting] = useState(null);
  const [verifying, setVerifying] = useState(null);

  const load = () => API.get("/stores").then((r) => setStores(r.data.stores)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const verify = async (s) => {
    setVerifying(s.id);
    try {
      const r = await API.post(`/stores/${s.id}/verify`);
      if (r.data.status === "connected") toast.success(`${s.name}: ${r.data.message}`);
      else toast.error(`${s.name}: ${r.data.message}`, { duration: 8000 });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerifying(null);
    }
  };

  const remove = async () => {
    try {
      await API.delete(`/stores/${deleting.id}`);
      toast.success(`Store ${deleting.name} eliminato`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="stores-screen">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Store collegati</h1>
          <p className="text-sm text-muted-foreground">
            Le credenziali vengono cifrate a riposo e non lasciano mai il server. Dopo aver inserito le chiavi usa{" "}
            <strong>Verifica connessione</strong>.
          </p>
        </div>
        <button
          data-testid="add-store-btn"
          onClick={() => setEditing("new")}
          className="flex h-12 items-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-bold text-white hover:bg-blue-700"
        >
          <Plus size={18} /> Aggiungi store
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {stores.map((s) => (
          <div key={s.id} data-testid={`store-card-${s.code}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-lg font-bold">
                  {s.name}
                  <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-xs font-semibold text-white">{s.code}</span>
                </div>
                <div className="mt-0.5 font-mono text-xs text-muted-foreground">{s.shop_domain}</div>
              </div>
              <ConnStatus status={s.status} testid={`store-status-${s.code}`} />
            </div>
            {s.status_message && (
              <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid={`store-error-${s.code}`}>
                {s.status_message}
              </div>
            )}
            <dl className="mb-4 space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between"><dt>ID interno</dt><dd className="font-mono">{s.id}</dd></div>
              <div className="flex justify-between"><dt>Client ID</dt><dd className="font-mono">{s.client_id || "—"}</dd></div>
              <div className="flex justify-between"><dt>Client secret</dt><dd>{s.has_client_secret ? "•••••••• (cifrato)" : "mancante"}</dd></div>
              <div className="flex justify-between"><dt>AuthCode Spediamo</dt><dd>{s.has_auth_code ? "•••••••• (cifrato)" : "mancante"}</dd></div>
              <div className="flex justify-between" title="SHA-256 dell'authCode, primi 10 caratteri: confronta fra store senza esporre il valore">
                <dt>Impronta authCode</dt>
                <dd className="font-mono" data-testid={`authcode-fp-${s.code}`}>{s.auth_code_fingerprint || "—"}</dd>
              </div>
              <div className="flex justify-between"><dt>Mittente</dt><dd>{s.sender?.city ? `${s.sender.city} (${s.sender.zip})` : "—"}</dd></div>
              <div className="flex justify-between"><dt>Collo</dt><dd className="font-mono">{s.parcel?.length}x{s.parcel?.width}x{s.parcel?.height} mm · {s.parcel?.weight} g</dd></div>
              <div className="flex justify-between"><dt>Corrieri</dt><dd>{(s.couriers || []).map((c) => c.service_code).join(", ") || "—"}</dd></div>
              <div className="flex justify-between"><dt>Email cliente Shopify</dt><dd>{s.notify_customer ? "attiva" : "disattivata"}</dd></div>
              <div className="flex justify-between"><dt>Ultima verifica</dt><dd>{formatDateTime(s.last_check_at)}</dd></div>
            </dl>
            <div className="flex gap-2">
              <button
                data-testid={`verify-btn-${s.code}`}
                onClick={() => verify(s)}
                disabled={verifying === s.id}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                <Plug size={15} /> {verifying === s.id ? "Verifica…" : "Verifica connessione"}
              </button>
              <button
                data-testid={`edit-store-btn-${s.code}`}
                onClick={() => setEditing(s)}
                className="rounded-lg border border-slate-300 p-2.5 text-slate-600 hover:bg-slate-100"
                title="Modifica store"
              >
                <Pencil size={16} />
              </button>
              <button
                data-testid={`delete-store-btn-${s.code}`}
                onClick={() => setDeleting(s)}
                className="rounded-lg border border-slate-300 p-2.5 text-slate-600 hover:bg-rose-50 hover:text-rose-700"
                title="Elimina store"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {stores.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-muted-foreground" data-testid="stores-empty">
            Nessuno store collegato. Aggiungi il primo store per iniziare.
          </div>
        )}
      </div>

      <IntegrationKeys />

      {editing && (
        <StoreForm
          store={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="delete-store-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-3 text-xl font-bold">Elimina store</h2>
            <p className="text-sm">
              Eliminare <strong>{deleting.name}</strong>? Le spedizioni e i log storici restano archiviati, ma lo store non sara piu utilizzabile.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="delete-store-no" onClick={() => setDeleting(null)} className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100">
                Annulla
              </button>
              <button data-testid="delete-store-yes" onClick={remove} className="rounded-lg bg-rose-600 px-6 py-3 text-sm font-bold text-white hover:bg-rose-700">
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StoreForm({ store, onClose, onSaved }) {
  const isNew = !store;
  const [form, setForm] = useState(() => {
    if (!store) return EMPTY_FORM;
    return {
      name: store.name,
      code: store.code,
      shop_domain: store.shop_domain,
      client_id: store.client_id,
      client_secret: "",
      spediamo_auth_code: "",
      sender: { ...EMPTY_FORM.sender, ...(store.sender || {}) },
      parcel: { ...EMPTY_FORM.parcel, ...(store.parcel || {}) },
      couriers: store.couriers?.length ? store.couriers : EMPTY_FORM.couriers,
      default_courier: store.default_courier || "SDAEXP",
      notify_customer: store.notify_customer !== false,
    };
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSender = (k, v) => setForm((f) => ({ ...f, sender: { ...f.sender, [k]: v } }));
  const setParcel = (k, v) => setForm((f) => ({ ...f, parcel: { ...f.parcel, [k]: parseInt(v) || 0 } }));

  const save = async () => {
    setError("");
    if (isNew && (!form.name || !form.code || !form.shop_domain || !form.client_id || !form.client_secret || !form.spediamo_auth_code)) {
      setError("Compila tutti i campi obbligatori: nome, codice, dominio, client_id, client_secret, authCode Spediamo Pro.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isNew) {
        if (!payload.client_secret) delete payload.client_secret;
        if (!payload.spediamo_auth_code) delete payload.spediamo_auth_code;
      }
      if (isNew) await API.post("/stores", payload);
      else await API.put(`/stores/${store.id}`, payload);
      toast.success(isNew ? "Store aggiunto. Ora verifica la connessione." : "Store aggiornato");
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const input = (label, value, onChange, testid, opts = {}) => (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        {...opts}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="store-form-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-4 text-xl font-bold">{isNew ? "Aggiungi store" : `Modifica ${store.name}`}</h2>

        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dati store e credenziali</h3>
        <div className="mb-5 grid grid-cols-2 gap-3">
          {input("Nome *", form.name, (v) => set("name", v), "store-form-name", { placeholder: "Sport4Wear" })}
          {input("Codice breve *", form.code, (v) => set("code", v.toUpperCase()), "store-form-code", { placeholder: "S4W" })}
          {input("Dominio Shopify *", form.shop_domain, (v) => set("shop_domain", v), "store-form-domain", { placeholder: "sport4wear.myshopify.com" })}
          {input("Client ID *", form.client_id, (v) => set("client_id", v), "store-form-client-id")}
          {input(
            isNew ? "Client secret *" : "Client secret (vuoto = invariato)",
            form.client_secret,
            (v) => set("client_secret", v),
            "store-form-client-secret",
            { type: "password", autoComplete: "new-password" }
          )}
          {input(
            isNew ? "AuthCode Spediamo Pro *" : "AuthCode Spediamo Pro (vuoto = invariato)",
            form.spediamo_auth_code,
            (v) => set("spediamo_auth_code", v),
            "store-form-auth-code",
            { type: "password", autoComplete: "new-password" }
          )}
        </div>

        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mittente</h3>
        <div className="mb-5 grid grid-cols-2 gap-3">
          {input("Ragione sociale", form.sender.name, (v) => setSender("name", v), "sender-name")}
          {input("Email", form.sender.email, (v) => setSender("email", v), "sender-email")}
          {input("Indirizzo", form.sender.address, (v) => setSender("address", v), "sender-address")}
          {input("Secondo rigo", form.sender.address2, (v) => setSender("address2", v), "sender-address2")}
          {input("CAP", form.sender.zip, (v) => setSender("zip", v), "sender-zip")}
          {input("Citta", form.sender.city, (v) => setSender("city", v), "sender-city")}
          {input("Provincia", form.sender.province, (v) => setSender("province", v), "sender-province")}
          {input("Telefono", form.sender.phone, (v) => setSender("phone", v), "sender-phone")}
        </div>

        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Collo standard (mm / grammi)</h3>
        <div className="mb-5 grid grid-cols-4 gap-3">
          {input("Lunghezza", form.parcel.length, (v) => setParcel("length", v), "parcel-length", { type: "number" })}
          {input("Larghezza", form.parcel.width, (v) => setParcel("width", v), "parcel-width", { type: "number" })}
          {input("Altezza", form.parcel.height, (v) => setParcel("height", v), "parcel-height", { type: "number" })}
          {input("Peso (g)", form.parcel.weight, (v) => setParcel("weight", v), "parcel-weight", { type: "number" })}
        </div>

        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Corrieri</h3>
        <div className="mb-5 space-y-2">
          {form.couriers.map((c, idx) => (
            <div key={idx} className="grid grid-cols-3 items-end gap-3">
              {input("Nome", c.name, (v) => setForm((f) => ({ ...f, couriers: f.couriers.map((x, i) => (i === idx ? { ...x, name: v } : x)) })), `courier-name-${idx}`)}
              {input("Service code", c.service_code, (v) => setForm((f) => ({ ...f, couriers: f.couriers.map((x, i) => (i === idx ? { ...x, service_code: v } : x)) })), `courier-code-${idx}`)}
              <label className="flex h-10 items-center gap-2 text-sm font-semibold">
                <input
                  type="radio"
                  name="default_courier"
                  data-testid={`courier-default-${idx}`}
                  checked={form.default_courier === c.service_code}
                  onChange={() => set("default_courier", c.service_code)}
                />
                Predefinito
              </label>
            </div>
          ))}
        </div>

        <label className="mb-5 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold">
          <input
            type="checkbox"
            data-testid="store-form-notify"
            checked={form.notify_customer}
            onChange={(e) => set("notify_customer", e.target.checked)}
            className="h-4 w-4"
          />
          Invia email di spedizione Shopify al cliente (notifyCustomer)
        </label>

        {error && (
          <div data-testid="store-form-error" className="mb-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button data-testid="store-form-cancel" onClick={onClose} className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100">
            Annulla
          </button>
          <button
            data-testid="store-form-save"
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? "Salvataggio…" : isNew ? "Aggiungi store" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}
